import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";

import { getPattern } from "./ChartMain";
interface ChartWithId extends am5xy.XYChart {
  id: string;
  data: BarDataItem[];
}
interface RootWithId extends am5.Root {
  id: string;
}
interface BarChartConfig {
  id: string;
  filepath: string;
  dataString: string;
  percent?: boolean;
}

interface BarDataItem {
  name: string;
  value: number;
  tooltiptext: string;
  color: am5.Color;
  pattern?: am5.Pattern;
}

declare const barchartarray: BarChartConfig[];

const barCharts: ChartWithId[] = [];
const barChartRoots: RootWithId[] = [];

if (typeof barchartarray !== "undefined") {
  for (const x of barchartarray) {
    am5.net.load(x.filepath).then((result) => {

      // Create root element
      // https://www.amcharts.com/docs/v5/getting-started/#Root_element
      const root = am5.Root.new(x.id) as RootWithId;

      let data: BarDataItem[] = [];

      if (x.filepath.includes(".csv")) {
        if (result.response) {
          const delimiter = result.response.split(";").length === 1 ? "," : ";";

          data = am5.CSVParser.parse(result.response, {
            delimiter,
            useColumnNames: true,
          }) as BarDataItem[];
        }
      } else if (x.dataString) {
        const dataArr = x.dataString.split(",");
        for (let i = 0; i < dataArr.length; i += 5) {
          data.push({
            name: dataArr[i],
            value: parseFloat(dataArr[i + 1]),
            tooltiptext: dataArr[i + 2],
            color: am5.color(dataArr[i + 3]),
            pattern: getPattern(dataArr[i + 3], dataArr[i + 4], root),
          });
        }
      }

      // Set themes
      // https://www.amcharts.com/docs/v5/concepts/themes/
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      // https://www.amcharts.com/docs/v5/charts/xy-chart/
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: "panX",
          wheelY: "zoomX",
        })
      ) as ChartWithId;

      chart.zoomOutButton.set("exportable", false);

      // Create axes
      // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
      const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
      xRenderer.labels.template.setAll({
        rotation: -45,
        centerY: am5.p50,
        centerX: am5.p50,
      });

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "name",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      );

      let yAxis: am5xy.ValueAxis<am5xy.AxisRendererY>;
      if (x.percent) {
        yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            min: 0,
            max: 100,
            numberFormat: "#'%'",
            strictMinMax: true,
            calculateTotals: true,
            renderer: am5xy.AxisRendererY.new(root, {}),
          })
        );
      } else {
        yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            min: 0,
            renderer: am5xy.AxisRendererY.new(root, {}),
          })
        );
      }

      // Add cursor
      chart.set(
        "cursor",
        am5xy.XYCursor.new(root, {
          behavior: "zoomX",
        })
      );

      xAxis.set(
        "tooltip",
        am5.Tooltip.new(root, {
          themeTags: ["axis"],
        })
      );

      yAxis.set(
        "tooltip",
        am5.Tooltip.new(root, {
          themeTags: ["axis"],
        })
      );

      // Add scrollbar
      // https://www.amcharts.com/docs/v5/charts/xy-chart/scrollbars/
      chart.set(
        "scrollbarX",
        am5.Scrollbar.new(root, {
          orientation: "horizontal",
          exportable: false,
        })
      );

      // Localization strings
      root.locale["From %1"] =
        "Anv�nd piltangenterna f�r att zooma in och ut i diagrammet. Zoomfunktionen fungerar inte n�r ett sk�rmavl�sningsprogram �r aktivt. %1";
      root.locale["To %1"] =
        "Anv�nd piltangenterna f�r att zooma in och ut i diagrammet. Zoomfunktionen fungerar inte n�r ett sk�rmavl�sningsprogram �r aktivt. %1";

      // Create series
      // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: "Series",
          xAxis,
          yAxis,
          valueYField: "value",
          categoryXField: "name",
          sequencedInterpolation: true,
          tooltip: am5.Tooltip.new(root, {
            labelText: "{value}",
          }),
        })
      );

      series.columns.template.adapters.add("fill", (fill, target) => {
        const data = target.dataItem?.dataContext as BarDataItem;
        return data?.color ?? fill;
      });

      series.columns.template.adapters.add("stroke", (stroke, target) => {
        const data = target.dataItem?.dataContext as BarDataItem;
        return data?.color ?? stroke;
      });

      series.columns.template.adapters.add("fillPattern", (fill, target) => {
        const data = target.dataItem?.dataContext as BarDataItem;
        return data?.pattern ?? fill;
      });

      const tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        autoTextColor: false,
        labelText: "{tooltiptext}",
      });

      // Set the background fill color
      tooltip.get("background")?.setAll({
        fill: am5.color(0x696969)
      });

      // Set the label text color
      tooltip.label.setAll({
        fill: am5.color(0xffffff)
      });

      tooltip.get("background")?.setAll({
        fill: am5.color("#696969"),
      });

      series.set("tooltip", tooltip);

      series.columns.template.setAll({
        focusable: true,
        ariaLabel: "{name}. {value}. {tooltiptext}.",
      });

      series.data.processor = am5.DataProcessor.new(root, {
        numericFields: ["value"],
      });

      xAxis.data.setAll(data);
      series.data.setAll(data);

      // Animate on load
      // https://www.amcharts.com/docs/v5/concepts/animations/
      series.appear();
      chart.appear(1000, 100);

      chart.id = x.id;
      chart.data = data;

      root.id = x.id;

      barCharts.push(chart);
      barChartRoots.push(root);
    });
  };
}
  export function barChartDropDown(element: HTMLElement) {
    element.nextElementSibling?.classList.toggle("show");
    const dropdown = element.nextElementSibling;
    dropdown?.classList.toggle("show");
  }

export function exportBarChart(format: "png" | "jpg" | "pdf" | "xlsx" | "csv" | "json" | "pdfdata", id: string) {
    const chart = barCharts.find((chrt) => chrt.id === id);
    const root = barChartRoots.find((rt) => rt.id === id);
    if (chart && root) {
      const exporting = am5plugins_exporting.Exporting.new(root, {
        dataSource: (chart as any).data,
        filePrefix: "Stapeldiagram",
      });
      exporting.download(format);
    }
  }

